import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, onSnapshot, query, where, doc, updateDoc } from 'firebase/firestore';
import { FaQrcode, FaUtensils, FaBox } from 'react-icons/fa';
import './DeliveryStatusPage.css';

const DeliveryStatusPage = () => {
  const { user: currentUser } = useAuth();

  const [deliveryStatuses, setDeliveryStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scannedCustomer, setScannedCustomer] = useState(null);
  const [updatingDelivery, setUpdatingDelivery] = useState(null);

  const location = useLocation();

  useEffect(() => {
    if (!currentUser) return;

    let deliveryQuery;

    if (scannedCustomer) {
      deliveryQuery = query(
        collection(db, 'delivery_status'),
        where('userId', '==', scannedCustomer.id)
      );
    } else {
      if (currentUser.docId) {
        deliveryQuery = query(
          collection(db, 'delivery_status'),
          where('userId', '==', currentUser.docId)
        );
      } else {
        setDeliveryStatuses([]);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    const unsubscribe = onSnapshot(
      deliveryQuery,
      (snapshot) => {
        const deliveryData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setDeliveryStatuses(deliveryData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching delivery statuses:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [scannedCustomer, currentUser]);

  const determineStatus = (deliveryStatus) => {
    if (deliveryStatus.status === 'vessel_collected') return 'Vessel Collected';
    if (deliveryStatus.status === 'vessel_placed') return 'Vessel Placed';
    if (deliveryStatus.status === 'food_delivered') return 'Food Delivered';
    if (deliveryStatus.status === 'food_collected') return 'Food Collected';
    if (deliveryStatus.status === 'on_the_way') return 'On The Way';
    return deliveryStatus.status || 'Pending';
  };

  const handleUpdateDeliveryStatus = async (deliveryId, newStatus) => {
    if (!deliveryId) return;
    setUpdatingDelivery(deliveryId);
    try {
      const deliveryRef = doc(db, 'delivery_status', deliveryId);
      const updateData = {
        status: newStatus,
        locationId: scannedCustomer?.locationId || '',
        userId: currentUser?.docId || '',
        userName: currentUser?.name || '',
      };
      await updateDoc(deliveryRef, updateData);
      alert(`Delivery status updated successfully!`);
    } catch (error) {
      console.error('Error updating delivery status:', error);
      alert('Failed to update delivery status.');
    } finally {
      setUpdatingDelivery(null);
    }
  };

  if (loading && deliveryStatuses.length === 0) {
    return <div>Loading your delivery status...</div>;
  }

  return (
    <div className="delivery-page-container">
      <div className="delivery-header">
        <div>
          <h1>Delivery Status</h1>
          <p>Welcome, {currentUser?.name}! Here is the status of your deliveries.</p>
        </div>
        <Link to="/user/scan-qr" className="scan-button"><FaQrcode /> Scan an Order</Link>
      </div>

      {scannedCustomer && deliveryStatuses.length > 0 && (
        <div className="action-buttons-container">
          <h3>Update Delivery Status</h3>
          <div className="action-buttons">
            <button
              onClick={() => handleUpdateDeliveryStatus(deliveryStatuses[0].id, 'food_collected')}
              disabled={updatingDelivery === deliveryStatuses[0].id}
              className="action-btn food-collected-btn"
            >
              <FaUtensils /> Food Collected
            </button>
            <button
              onClick={() => handleUpdateDeliveryStatus(deliveryStatuses[0].id, 'vessel_placed')}
              disabled={updatingDelivery === deliveryStatuses[0].id}
              className="action-btn vessel-placed-btn"
            >
              <FaBox /> Vessel Placed
            </button>
          </div>
        </div>
      )}

      <div className="delivery-list">
        {deliveryStatuses.length === 0 ? (
          <div className="no-orders">
            <p>You have no active deliveries at the moment.</p>
          </div>
        ) : (
          deliveryStatuses.map((deliveryStatus) => (
            <div key={deliveryStatus.id} className="delivery-item">
              <div className="delivery-detail">
                <span className="detail-label">Delivery ID</span>
                <span>{deliveryStatus.deliveryId}</span>
              </div>
              <div className="delivery-detail">
                <span className="detail-label">Delivery Person</span>
                <span>{deliveryStatus.deliveryPersonName || 'N/A'}</span>
              </div>
              <div className="delivery-detail">
                <span className="detail-label">Status</span>
                <span className="status-highlight">{determineStatus(deliveryStatus)}</span>
              </div>
              <div className="delivery-detail">
                <span className="detail-label">Last Updated</span>
                <span>{deliveryStatus.updatedAt?.toDate().toLocaleString() || 'N/A'}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DeliveryStatusPage;
